import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import gsap from 'gsap';
import Toast from './Toast';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useAsyncAPI } from '../hooks/useAPI';
import authVisual from '../assets/auth/buyer-auth-visual.png';

// Light-colored logo variant — reads correctly against this page's dark background.
const logo = '/autoboy_logo3.png';

const TRUST_CHIPS = [
  { icon: 'fa-shield-halved', label: 'Escrow Protected' },
  { icon: 'fa-users', label: '50K+ Active Buyers' },
  { icon: 'fa-box-open', label: '10K+ Products' },
];

const LoginSignup = () => {
  const navigate = useNavigate();
  const [isSignup, setIsSignup] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    rememberMe: false,
    agreeToTerms: false
  });
  const [showPassword, setShowPassword] = useState({
    login: false,
    signup: false,
    confirm: false
  });
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);
  const [sendingReset, setSendingReset] = useState(false);

  const visualRef = useRef(null);
  const formWrapperRef = useRef(null);

  // Smooth crossfade whenever the form switches between login/signup —
  // also doubles as the entrance animation on first mount.
  useEffect(() => {
    gsap.fromTo(
      formWrapperRef.current,
      { opacity: 0, y: 18 },
      { opacity: 1, y: 0, duration: 0.55, ease: 'power3.out' }
    );
  }, [isSignup]);

  // Subtle mouse-follow parallax on the hero visual — alive, not distracting.
  useEffect(() => {
    const handleMouseMove = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 24;
      const y = (e.clientY / window.innerHeight - 0.5) * 24;
      gsap.to(visualRef.current, { x, y, duration: 1.2, ease: 'power2.out' });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const togglePassword = (field) => {
    setShowPassword(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Must match the backend's IsValidPassword requirement exactly (min 8
  // chars + uppercase + lowercase + number + special char), otherwise a
  // password that looks valid here gets rejected by the API.
  const validatePassword = (password) => {
    return password.length >= 8 &&
      /[A-Z]/.test(password) &&
      /[a-z]/.test(password) &&
      /[0-9]/.test(password) &&
      /[^A-Za-z0-9]/.test(password);
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^(\+234|0)[789][01]\d{8}$/;
    return phoneRegex.test(phone);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (isSignup && !validatePassword(formData.password)) {
      // Complexity is only enforced at signup, matching the backend's
      // IsValidPassword rule. Login must never re-validate complexity —
      // an existing account's password is correct-or-not, not a shape to check.
      newErrors.password = 'Password must be 8+ characters with uppercase, lowercase, a number, and a special character';
    }

    if (isSignup) {
      if (!formData.fullName) {
        newErrors.fullName = 'Full name is required';
      }
      if (!formData.phoneNumber) {
        newErrors.phoneNumber = 'Phone number is required';
      } else if (!validatePhone(formData.phoneNumber)) {
        newErrors.phoneNumber = 'Please enter a valid Nigerian phone number';
      }
      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm your password';
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match';
      }
      if (!formData.agreeToTerms) {
        newErrors.agreeToTerms = 'You must agree to the Terms and Conditions to sign up';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const { login } = useAuth();
  const { execute, loading: apiLoading } = useAsyncAPI();

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      const response = await execute(() => login({
        email: formData.email,
        password: formData.password
      }));

      if (response?.data?.requires_2fa || response?.requires_2fa) {
        setToast({
          message: '2FA required. Please check your SMS/Email for the verification code.',
          type: 'info'
        });
        return;
      }

      setToast({
        message: 'Login successful! Welcome back.',
        type: 'success'
      });

      setTimeout(() => {
        navigate('/homepage');
      }, 1500);

    } catch (error) {
      setErrors({ general: error.message || 'Login failed' });
    }
  };

  const { register } = useAuth();

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      const nameParts = formData.fullName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || nameParts[0];

      let formattedPhone = formData.phoneNumber.trim();
      if (formattedPhone.startsWith('0')) {
        formattedPhone = '+234' + formattedPhone.substring(1);
      } else if (!formattedPhone.startsWith('+')) {
        formattedPhone = '+234' + formattedPhone;
      }

      const registrationData = {
        email: formData.email,
        password: formData.password,
        first_name: firstName,
        last_name: lastName,
        username: formData.email.split('@')[0].length < 3
          ? formData.email.replace(/[@.]/g, '').substring(0, 30)
          : formData.email.split('@')[0].substring(0, 30),
        phone: formattedPhone,
        user_type: 'buyer',
        accept_terms: true
      };

      const response = await execute(() => register(registrationData));

      const token = response.data?.token || response.token;

      if (token) {
        setToast({
          message: 'Account created! Welcome to AutoboyExpress.',
          type: 'success'
        });

        setTimeout(() => {
          navigate('/homepage');
        }, 1500);
      } else {
        setToast({
          message: 'Registration successful! Please check your email to verify your account before logging in.',
          type: 'success'
        });

        setTimeout(() => {
          setIsSignup(false);
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }, 3000);
      }

    } catch (error) {
      setToast({
        message: error.message || 'Registration failed. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Sends a reset link to whatever address is already typed in the email
  // field, so the user doesn't have to re-enter it.
  const handleForgotPassword = async () => {
    if (!formData.email || !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Enter your email address first, then tap Forgot Password' }));
      return;
    }

    setSendingReset(true);
    try {
      await authAPI.forgotPassword(formData.email);
      // The backend deliberately gives the same answer whether or not the
      // address exists, so the wording here must not imply it found an account.
      setToast({
        message: 'If that email is registered, a reset link is on its way. Check your inbox.',
        type: 'success'
      });
    } catch (error) {
      setToast({ message: error.message || 'Could not send reset link. Please try again.', type: 'error' });
    } finally {
      setSendingReset(false);
    }
  };

  const toggleForms = () => {
    setIsSignup(!isSignup);
    setFormData({
      email: '',
      fullName: '',
      phoneNumber: '',
      password: '',
      confirmPassword: '',
      rememberMe: false,
      agreeToTerms: false
    });
    setErrors({});
    setShowPassword({
      login: false,
      signup: false,
      confirm: false
    });
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    try {
      const response = await authAPI.googleLogin(credentialResponse.credential);

      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('refreshToken', response.data.refresh_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));

      setToast({
        message: 'Signed in with Google successfully!',
        type: 'success'
      });

      setTimeout(() => {
        navigate('/homepage');
        window.location.reload();
      }, 1500);
    } catch (error) {
      setToast({
        message: error.message || 'Google login failed.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    setToast({
      message: 'Google login failed. Please try again.',
      type: 'error'
    });
  };

  const activePasswordField = isSignup ? 'signup' : 'login';
  const busy = isLoading || apiLoading;

  return (
    <div className="tw-min-h-dvh tw-w-full tw-bg-[#0A0A0A] tw-flex tw-flex-col lg:tw-flex-row tw-font-['Epilogue',_sans-serif]">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Left — Brand Visual */}
      <div className="tw-relative tw-hidden lg:tw-flex lg:tw-w-[46%] xl:tw-w-1/2 tw-flex-col tw-justify-between tw-overflow-hidden tw-bg-black">
        <img
          ref={visualRef}
          src={authVisual}
          alt=""
          aria-hidden="true"
          className="tw-absolute tw-inset-0 tw-h-[110%] tw-w-[110%] -tw-top-[5%] -tw-left-[5%] tw-object-cover tw-scale-105"
        />
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-t tw-from-black tw-via-black/60 tw-to-black/20" />
        <div className="tw-absolute tw-inset-0 tw-bg-gradient-to-r tw-from-black/40 tw-via-transparent tw-to-transparent" />

        <div className="tw-relative tw-z-10 tw-p-10 xl:tw-p-14">
          <a href="/homepage" className="tw-inline-flex tw-items-center tw-gap-2 tw-text-white/90 hover:tw-text-white tw-transition-colors tw-text-sm tw-font-medium">
            <i className="fas fa-arrow-left tw-text-xs" />
            Back to shop
          </a>
        </div>

        <div className="tw-relative tw-z-10 tw-p-10 xl:tw-p-14 tw-pb-14 xl:tw-pb-16">
          <img src={logo} alt="AutoboyExpress" className="tw-h-36 xl:tw-h-44 tw-w-auto tw-mb-8 tw-drop-shadow-[0_2px_28px_rgba(34,197,94,0.45)]" />
          <h1 className="tw-text-[2.5rem] xl:tw-text-[3rem] tw-font-extrabold tw-leading-[1.05] tw-tracking-tight tw-text-white tw-mb-4">
            Nigeria's trusted<br />marketplace for<br />
            <span className="tw-bg-gradient-to-r tw-from-green-400 tw-to-green-500 tw-bg-clip-text tw-text-transparent">phones &amp; gadgets.</span>
          </h1>
          <p className="tw-text-white/60 tw-text-base tw-max-w-md tw-mb-9 tw-leading-relaxed">
            Verified sellers, secure escrow, and delivery you can track — on every order.
          </p>
          <div className="tw-flex tw-flex-wrap tw-gap-3">
            {TRUST_CHIPS.map((chip) => (
              <div
                key={chip.label}
                className="tw-flex tw-items-center tw-gap-2 tw-rounded-full tw-border tw-border-white/10 tw-bg-white/[0.06] tw-backdrop-blur-xl tw-px-4 tw-py-2 tw-text-xs tw-font-semibold tw-text-white/85"
              >
                <i className={`fas ${chip.icon} tw-text-green-400`} />
                {chip.label}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right — Form */}
      <div className="tw-flex tw-flex-1 tw-flex-col tw-items-center tw-justify-center tw-bg-[#0A0A0A] tw-px-6 tw-py-10 sm:tw-px-10 lg:tw-px-16 tw-min-h-dvh tw-overflow-y-auto">
        {/* Mobile-only top bar */}
        <div className="tw-flex lg:tw-hidden tw-w-full tw-max-w-[420px] tw-items-center tw-justify-between tw-mb-8">
          <a href="/homepage" className="tw-inline-flex tw-items-center tw-gap-2 tw-text-white/60 hover:tw-text-white tw-transition-colors tw-text-sm tw-font-medium">
            <i className="fas fa-arrow-left tw-text-xs" />
            Back
          </a>
          <img src={logo} alt="AutoboyExpress" className="tw-h-20 tw-w-auto" />
        </div>

        <div ref={formWrapperRef} className="tw-w-full tw-max-w-[420px] tw-py-6">
          <img src={logo} alt="AutoboyExpress" className="tw-hidden lg:tw-block tw-h-28 tw-w-auto tw-mb-6 tw-mx-auto" />

          <h2 className="tw-text-3xl sm:tw-text-4xl tw-font-bold tw-text-white tw-mb-1.5 tw-tracking-tight">
            {isSignup ? 'Create your account' : 'Welcome back'}
          </h2>
          <p className="tw-text-white/45 tw-text-sm tw-mb-8">
            {isSignup ? 'Join AutoboyExpress to start shopping.' : 'Sign in to continue to your account.'}
          </p>

          <form
            className="tw-flex tw-flex-col tw-gap-5"
            onSubmit={isSignup ? handleSignup : handleLogin}
          >
            {/* Email */}
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-white/80 tw-mb-2">Email Address</label>
              <div className="tw-relative">
                <i className="fas fa-envelope tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-11 tw-pr-4 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10"
                  placeholder="you@example.com"
                  required
                />
              </div>
              {errors.email && <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{errors.email}</p>}
            </div>

            {isSignup && (
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-white/80 tw-mb-2">Full Name</label>
                <div className="tw-relative">
                  <i className="fas fa-user tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
                  <input
                    type="text"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-11 tw-pr-4 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10"
                    placeholder="Enter your full name"
                    required
                  />
                </div>
                {errors.fullName && <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{errors.fullName}</p>}
              </div>
            )}

            {isSignup && (
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-white/80 tw-mb-2">Phone Number</label>
                <div className="tw-relative">
                  <i className="fas fa-phone tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleInputChange}
                    className="tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-11 tw-pr-4 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10"
                    placeholder="0801 234 5678"
                    required
                  />
                </div>
                {errors.phoneNumber && <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{errors.phoneNumber}</p>}
              </div>
            )}

            {/* Password */}
            <div>
              <label className="tw-block tw-text-sm tw-font-medium tw-text-white/80 tw-mb-2">
                {isSignup ? 'Create Password' : 'Password'}
              </label>
              <div className="tw-relative">
                <i className="fas fa-lock tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
                <input
                  type={showPassword[activePasswordField] ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-11 tw-pr-11 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10"
                  placeholder={isSignup ? 'Create a password' : 'Enter your password'}
                  required
                />
                <button
                  type="button"
                  className="tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/35 hover:tw-text-green-400 tw-transition-colors"
                  onClick={() => togglePassword(activePasswordField)}
                  aria-label={showPassword[activePasswordField] ? 'Hide password' : 'Show password'}
                >
                  <i className={`far ${showPassword[activePasswordField] ? 'fa-eye-slash' : 'fa-eye'}`} />
                </button>
              </div>
              {errors.password && <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{errors.password}</p>}
            </div>

            {isSignup && (
              <div>
                <label className="tw-block tw-text-sm tw-font-medium tw-text-white/80 tw-mb-2">Confirm Password</label>
                <div className="tw-relative">
                  <i className="fas fa-lock tw-absolute tw-left-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/25 tw-text-sm" />
                  <input
                    type={showPassword.confirm ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="tw-w-full tw-rounded-xl tw-border tw-border-white/10 tw-bg-white/[0.04] tw-pl-11 tw-pr-11 tw-py-3.5 tw-text-white tw-placeholder-white/25 tw-outline-none tw-transition-all focus:tw-border-green-500 focus:tw-bg-white/[0.06] focus:tw-ring-4 focus:tw-ring-green-500/10"
                    placeholder="Confirm your password"
                    required
                  />
                  <button
                    type="button"
                    className="tw-absolute tw-right-4 tw-top-1/2 -tw-translate-y-1/2 tw-text-white/35 hover:tw-text-green-400 tw-transition-colors"
                    onClick={() => togglePassword('confirm')}
                    aria-label={showPassword.confirm ? 'Hide password' : 'Show password'}
                  >
                    <i className={`far ${showPassword.confirm ? 'fa-eye-slash' : 'fa-eye'}`} />
                  </button>
                </div>
                {errors.confirmPassword && <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{errors.confirmPassword}</p>}
              </div>
            )}

            {isSignup && (
              <div>
                <label className="tw-flex tw-items-start tw-gap-2.5 tw-cursor-pointer">
                  <input
                    type="checkbox"
                    id="agreeToTerms"
                    name="agreeToTerms"
                    checked={formData.agreeToTerms}
                    onChange={handleInputChange}
                    className="tw-mt-0.5 tw-h-4 tw-w-4 tw-rounded tw-accent-green-500 tw-cursor-pointer tw-shrink-0"
                  />
                  <span className="tw-text-sm tw-text-white/60 tw-leading-relaxed">
                    I agree to the{' '}
                    <a href="/terms-and-conditions" target="_blank" rel="noopener noreferrer" className="tw-text-green-400 hover:tw-text-green-300 tw-font-semibold tw-underline tw-underline-offset-2">
                      Terms and Conditions
                    </a>
                    {' '}and{' '}
                    <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="tw-text-green-400 hover:tw-text-green-300 tw-font-semibold tw-underline tw-underline-offset-2">
                      Privacy Policy
                    </a>
                  </span>
                </label>
                {errors.agreeToTerms && <p className="tw-mt-1.5 tw-text-xs tw-font-medium tw-text-red-400">{errors.agreeToTerms}</p>}
              </div>
            )}

            {!isSignup && (
              <div className="tw-flex tw-items-center tw-justify-between">
                <label className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer">
                  <input
                    type="checkbox"
                    id="remember"
                    name="rememberMe"
                    checked={formData.rememberMe}
                    onChange={handleInputChange}
                    className="tw-h-4 tw-w-4 tw-rounded tw-accent-green-500 tw-cursor-pointer"
                  />
                  <span className="tw-text-sm tw-text-white/60">Remember me</span>
                </label>
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={sendingReset}
                  className="tw-bg-transparent tw-border-0 tw-p-0 tw-cursor-pointer tw-text-sm tw-font-medium tw-text-green-400 hover:tw-text-green-300 tw-transition-colors disabled:tw-opacity-60"
                >
                  {sendingReset ? 'Sending...' : 'Forgot Password?'}
                </button>
              </div>
            )}

            {errors.general && (
              <div className="tw-rounded-lg tw-bg-red-500/10 tw-border tw-border-red-500/20 tw-px-4 tw-py-2.5 tw-text-center tw-text-sm tw-font-medium tw-text-red-400">
                {errors.general}
              </div>
            )}

            <button
              type="submit"
              disabled={busy}
              className="tw-mt-1 tw-w-full tw-rounded-xl tw-bg-gradient-to-r tw-from-green-500 tw-to-green-600 tw-py-3.5 tw-text-base tw-font-bold tw-text-white tw-shadow-lg tw-shadow-green-500/20 tw-transition-all hover:-tw-translate-y-0.5 hover:tw-shadow-xl hover:tw-shadow-green-500/30 active:tw-translate-y-0 active:tw-scale-[0.98] disabled:tw-opacity-60 disabled:tw-pointer-events-none"
            >
              {busy ? 'Please wait...' : (isSignup ? 'Sign Up' : 'Login')}
            </button>
          </form>

          <div className="tw-flex tw-items-center tw-gap-4 tw-my-7">
            <div className="tw-h-px tw-flex-1 tw-bg-gradient-to-r tw-from-transparent tw-to-white/15" />
            <span className="tw-text-xs tw-font-medium tw-text-white/40 tw-whitespace-nowrap">
              Or {isSignup ? 'sign up' : 'login'} with
            </span>
            <div className="tw-h-px tw-flex-1 tw-bg-gradient-to-l tw-from-transparent tw-to-white/15" />
          </div>

          <div className="tw-flex tw-justify-center [&_iframe]:!tw-rounded-full">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="filled_black"
              shape="pill"
              size="large"
              text="continue_with"
              width="360"
            />
          </div>

          <p className="tw-text-center tw-text-sm tw-text-white/45 tw-mt-8">
            {isSignup ? 'Have an account? ' : "Don't have an account? "}
            <a
              href="#"
              onClick={(e) => { e.preventDefault(); toggleForms(); }}
              className="tw-font-semibold tw-text-green-400 hover:tw-text-green-300 tw-transition-colors"
            >
              {isSignup ? 'Sign in' : 'Sign up'}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginSignup;
